import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  Eye,
  EyeOff,
  MessageSquare,
  Flag,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Search,
  BarChart3,
  TrendingUp,
  Users,
  Award,
  AlertTriangle,
  Image as ImageIcon,
  Video,
  Download,
  Calendar,
  MapPin,
  User,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Activity,
  Target,
  Zap
} from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const ReviewManagement = () => {
  const { user } = useAuth();
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const CACHE_DURATION = 10000; // 10 seconds cache

  // Helper function to get full name
  const getFullName = (userObj) => {
    if (!userObj) return 'Unknown';
    if (userObj.firstName || userObj.lastName) {
      return `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim();
    }
    return userObj.name || 'Unknown';
  };
  const [reviews, setReviews] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagReason, setFlagReason] = useState('INAPPROPRIATE_CONTENT');
  const [filters, setFilters] = useState({
    status: 'all',
    rating: 'all',
    hasMedia: 'all',
    dateRange: 'all',
    search: '',
    vendorSearch: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    flagged: 0,
    rejected: 0,
    averageRating: 0,
    withMedia: 0,
    totalVendors: 0
  });
  const [performanceData, setPerformanceData] = useState({
    monthlyReviews: [],
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    topVendors: [],
    recentTrends: []
  });

  // Fetch vendors list
  const fetchVendors = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('http://localhost:5000/api/admin/users?role=vendor', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVendors(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, []);

  // Fetch reviews data using existing admin rating endpoint
  const fetchReviews = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isRequestInProgress) {
      console.log('Request already in progress, skipping...');
      return;
    }

    // Check cache - don't fetch if we fetched recently
    const now = Date.now();
    if (now - lastFetchTime < CACHE_DURATION) {
      console.log('Using cached data, skipping fetch...');
      return;
    }

    try {
      setIsRequestInProgress(true);
      setLoading(true);

      // Add a small delay to prevent too many rapid requests
      await new Promise(resolve => setTimeout(resolve, 100));
      // Build params object, only including defined values
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', itemsPerPage.toString());

      if (filters.status !== 'all') {
        params.append('status', filters.status.toUpperCase());
      }

      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }

      // Add rating filter if specified
      if (filters.rating !== 'all') {
        params.append('minRating', filters.rating);
        params.append('maxRating', filters.rating);
      }

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`http://localhost:5000/api/admin/ratings?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 429) {
        // Implement exponential backoff for 429 errors
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        throw new Error('Server is busy. Retrying automatically...');
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Filter by media if needed
      let reviews = data.ratings || [];
      if (filters.hasMedia !== 'all') {
        reviews = reviews.filter(review => {
          const hasMedia = review.images && review.images.length > 0;
          return filters.hasMedia === 'true' ? hasMedia : !hasMedia;
        });
      }

      setReviews(reviews);

      // Get basic stats
      const basicStats = {
        total: data.totalRatings || reviews.length,
        pending: reviews.filter(r => r.adminReview?.status === 'PENDING').length,
        approved: reviews.filter(r => r.adminReview?.status === 'APPROVED').length,
        flagged: reviews.filter(r => r.adminReview?.status === 'FLAGGED').length,
        rejected: reviews.filter(r => r.adminReview?.status === 'REJECTED').length,
        withMedia: reviews.filter(r => r.images && r.images.length > 0).length,
        averageRating: data.averageRating || 0
      };

      setStats(basicStats);

      // Calculate rating distribution from current reviews
      const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      reviews.forEach(review => {
        if (review.overallRating >= 1 && review.overallRating <= 5) {
          ratingDistribution[review.overallRating]++;
        }
      });

      // Update performance data with rating distribution
      setPerformanceData(prev => ({
        ...prev,
        ratingDistribution
      }));

      // Update cache timestamp
      setLastFetchTime(Date.now());
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
      setIsRequestInProgress(false);
    }
  }, [currentPage, itemsPerPage, filters]);


  // Fetch performance analytics using existing stats endpoint
  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch('http://localhost:5000/api/admin/ratings/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Basic performance data structure
        setPerformanceData({
          monthlyReviews: [],
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          topVendors: [],
          recentTrends: []
        });

        // Update stats with fetched data
        setStats(prev => ({
          ...prev,
          ...data,
          averageRating: data.averageRating || prev.averageRating
        }));
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []); // Remove reviews dependency

  // Fetch vendors on mount
  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Initial load with reduced frequency
  useEffect(() => {
    // Only fetch on initial mount and significant changes
    const timeoutId = setTimeout(() => {
      fetchReviews();
    }, 1000); // Longer initial delay

    return () => clearTimeout(timeoutId);
  }, []); // Only on mount

  // Separate effect for filter changes with longer debounce
  useEffect(() => {
    if (reviews.length > 0) { // Only if we have initial data
      const timeoutId = setTimeout(() => {
        fetchReviews();
      }, 800); // Longer delay for filter changes

      return () => clearTimeout(timeoutId);
    }
  }, [currentPage, itemsPerPage, filters.status, filters.rating, filters.hasMedia]);

  // Even longer debounce for search
  useEffect(() => {
    if (reviews.length > 0 && filters.search) { // Only if we have data and search term
      const timeoutId = setTimeout(() => {
        fetchReviews();
      }, 1200); // Much longer delay for search

      return () => clearTimeout(timeoutId);
    }
  }, [filters.search]);

  // Debounce for vendor search (local filtering, no API call needed)
  useEffect(() => {
    // Vendor search is handled locally in filteredReviews, no need to fetch
    // This is just for resetting page to 1 when vendor filter changes
    if (filters.vendorSearch) {
      setCurrentPage(1);
    }
  }, [filters.vendorSearch]);

  // Delay analytics fetch even more
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAnalytics();
    }, 2000); // Wait 2 seconds before fetching analytics

    return () => clearTimeout(timeoutId);
  }, []); // Only run once on mount

  // Handle review status update with actual API call
  const handleStatusUpdate = async (reviewId, newStatus, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`http://localhost:5000/api/admin/ratings/${reviewId}/review`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus.toUpperCase(),
          notes: notes || ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update review status');
      }

      const data = await response.json();

      // Update local state with the updated review
      setReviews(prev => prev.map(review =>
        review._id === reviewId
          ? {
              ...review,
              adminReview: {
                ...review.adminReview,
                status: newStatus.toUpperCase(),
                notes,
                reviewedAt: new Date().toISOString(),
                reviewedBy: user._id
              },
              isPublic: newStatus.toUpperCase() === 'APPROVED'
            }
          : review
      ));

      toast.success(`Review ${newStatus.toLowerCase()} successfully`);
      setShowReviewModal(false);

      // Refresh the data to get updated stats
      setTimeout(() => {
        fetchReviews();
      }, 1000);
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error(error.message || 'Failed to update review status');
    }
  };

  // Handle flag review with actual API call
  const handleFlagReview = async (reviewId, reason = 'INAPPROPRIATE_CONTENT') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`http://localhost:5000/api/admin/ratings/${reviewId}/review`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'FLAGGED',
          flagReason: reason,
          notes: `Flagged for: ${reason.replace('_', ' ').toLowerCase()}`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to flag review');
      }

      const data = await response.json();

      // Update local state with the flagged review
      setReviews(prev => prev.map(review =>
        review._id === reviewId
          ? {
              ...review,
              adminReview: {
                ...review.adminReview,
                status: 'FLAGGED',
                flagReason: reason,
                reviewedAt: new Date().toISOString(),
                reviewedBy: user._id,
                notes: `Flagged for: ${reason.replace('_', ' ').toLowerCase()}`
              },
              isPublic: false
            }
          : review
      ));

      toast.success('Review flagged successfully');
      setShowReviewModal(false);

      // Refresh the data to get updated stats
      setTimeout(() => {
        fetchReviews();
      }, 1000);
    } catch (error) {
      console.error('Error flagging review:', error);
      toast.error(error.message || 'Failed to flag review');
    }
  };

  // Filter and sort reviews
  const filteredReviews = reviews.filter(review => {
    if (filters.status !== 'all' && review.adminReview?.status !== filters.status.toUpperCase()) return false;
    if (filters.rating !== 'all' && review.overallRating !== parseInt(filters.rating)) return false;

    // Vendor search filter
    if (filters.vendorSearch && filters.vendorSearch.trim()) {
      const vendorSearchLower = filters.vendorSearch.toLowerCase();
      const vendorName = getFullName(review.vendorId).toLowerCase();
      if (!vendorName.includes(vendorSearchLower)) return false;
    }

    if (filters.hasMedia !== 'all') {
      const hasMedia = review.images && review.images.length > 0;
      if (filters.hasMedia === 'true' && !hasMedia) return false;
      if (filters.hasMedia === 'false' && hasMedia) return false;
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        review.title?.toLowerCase().includes(searchLower) ||
        review.comment?.toLowerCase().includes(searchLower) ||
        review.vendorId?.firstName?.toLowerCase().includes(searchLower) ||
        review.vendorId?.lastName?.toLowerCase().includes(searchLower) ||
        review.customerId?.firstName?.toLowerCase().includes(searchLower) ||
        review.customerId?.lastName?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReviews = filteredReviews.slice(startIndex, startIndex + itemsPerPage);

  // Render star rating
  const renderStars = (rating, size = 'w-4 h-4') => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          className={`${size} ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  // Render status badge
  const renderStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      FLAGGED: { color: 'bg-red-100 text-red-800', icon: Flag },
      REJECTED: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  // Review Details Modal with improved close handling
  const ReviewModal = ({ review, isOpen, onClose }) => {
    // Handle escape key press
    React.useEffect(() => {
      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscapeKey);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      }

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        // Restore body scroll when modal closes
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose]);

    if (!isOpen || !review) return null;

    const handleBackdropClick = (e) => {
      // Only close if clicking the backdrop, not the modal content
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleBackdropClick}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          {/* Modal content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking modal content
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-medium text-gray-900">Review Details</h3>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                  }}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded"
                  aria-label="Close modal"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Review Information */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Overall Rating</label>
                    <div className="flex items-center space-x-2">
                      {renderStars(review.overallRating, 'w-6 h-6')}
                      <span className="text-xl font-semibold">{review.overallRating}/5</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                    <p className="text-gray-900">{review.title || 'No title provided'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Comment</label>
                    <p className="text-gray-700">{review.comment || 'No comment provided'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Would Recommend</label>
                    <div className="flex items-center">
                      {review.wouldRecommend ? (
                        <div className="flex items-center text-green-600">
                          <ThumbsUp className="w-5 h-5 mr-2" />
                          Yes
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <ThumbsDown className="w-5 h-5 mr-2" />
                          No
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Criteria Ratings */}
                  {review.criteria && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Ratings</label>
                      <div className="space-y-2">
                        {Object.entries(review.criteria).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center">
                            <span className="capitalize text-sm text-gray-600">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <div className="flex items-center space-x-2">
                              {renderStars(value)}
                              <span className="text-sm">{value}/5</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Media and Additional Info */}
                <div className="space-y-4">
                  {/* Review Images */}
                  {review.images && review.images.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Photos ({review.images.length})
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {review.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image.url}
                              alt={image.caption || 'Review photo'}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                              <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100" />
                            </div>
                            {image.caption && (
                              <p className="text-xs text-gray-600 mt-1 truncate">
                                {image.caption}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Positive/Negative Aspects */}
                  {(review.positiveAspects?.length > 0 || review.negativeAspects?.length > 0) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Feedback Tags</label>
                      <div className="space-y-2">
                        {review.positiveAspects?.length > 0 && (
                          <div>
                            <p className="text-xs text-green-600 font-medium mb-1">Positive</p>
                            <div className="flex flex-wrap gap-1">
                              {review.positiveAspects.map((aspect, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800"
                                >
                                  {aspect.replace('_', ' ').toLowerCase()}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {review.negativeAspects?.length > 0 && (
                          <div>
                            <p className="text-xs text-red-600 font-medium mb-1">Negative</p>
                            <div className="flex flex-wrap gap-1">
                              {review.negativeAspects.map((aspect, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800"
                                >
                                  {aspect.replace('_', ' ').toLowerCase()}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Job Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Job Information</label>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center text-sm">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        Customer: {getFullName(review.customerId) || 'Anonymous'}
                      </div>
                      <div className="flex items-center text-sm">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        Vendor: {getFullName(review.vendorId) || 'Unknown'}
                      </div>
                      <div className="flex items-center text-sm">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {new Date(review.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Admin Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Admin Review Status</label>
                    <div className="space-y-2">
                      {renderStatusBadge(review.adminReview?.status || 'PENDING')}
                      {review.adminReview?.notes && (
                        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {review.adminReview.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                {review.adminReview?.status === 'PENDING' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'APPROVED')}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => setShowFlagModal(true)}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Flag
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'REJECTED')}
                      className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </button>
                  </>
                )}
                {review.adminReview?.status === 'APPROVED' && (
                  <>
                    <button
                      onClick={() => setShowFlagModal(true)}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      <Flag className="w-4 h-4 mr-2" />
                      Flag
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'REJECTED')}
                      className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </button>
                  </>
                )}
                {review.adminReview?.status === 'FLAGGED' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'APPROVED')}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'REJECTED')}
                      className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </button>
                  </>
                )}
                {review.adminReview?.status === 'REJECTED' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'APPROVED')}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(review._id, 'PENDING')}
                      className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Reset to Pending
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  // Flag Review Modal
  const FlagModal = () => {
    const flagOptions = [
      { value: 'INAPPROPRIATE_CONTENT', label: 'Inappropriate Content' },
      { value: 'SPAM', label: 'Spam' },
      { value: 'FAKE_REVIEW', label: 'Fake Review' },
      { value: 'OFFENSIVE_LANGUAGE', label: 'Offensive Language' },
      { value: 'IRRELEVANT', label: 'Irrelevant' },
      { value: 'DUPLICATE', label: 'Duplicate' },
      { value: 'OTHER', label: 'Other' }
    ];

    if (!showFlagModal || !selectedReview) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowFlagModal(false)} />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
          >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Flag className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Flag Review
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      Why are you flagging this review? This will hide it from public view.
                    </p>
                    <select
                      value={flagReason}
                      onChange={(e) => setFlagReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                    >
                      {flagOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={async () => {
                  await handleFlagReview(selectedReview._id, flagReason);
                  setShowFlagModal(false);
                }}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Flag Review
              </button>
              <button
                type="button"
                onClick={() => setShowFlagModal(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 -mt-8">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-orange-600 to-orange-600 text-white py-20 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-3xl font-bold text-white">Review Management</h1>
              <p className="text-orange-100 mt-2">
                Monitor and manage customer reviews, ratings, and feedback with photos/videos
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{stats.total || 0}</div>
                <div className="text-sm text-orange-100">Total Reviews</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{stats.pending || 0}</div>
                <div className="text-sm text-orange-100">Pending Review</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{stats.averageRating?.toFixed(1) || '0.0'}</div>
                <div className="text-sm text-orange-100">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="flagged">Flagged</option>
                <option value="rejected">Rejected</option>
              </select>

              {/* Vendor Filter */}
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by vendor..."
                  value={filters.vendorSearch}
                  onChange={(e) => setFilters(prev => ({ ...prev, vendorSearch: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Rating Filter */}
              <select
                value={filters.rating}
                onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>

              {/* Media Filter */}
              <select
                value={filters.hasMedia}
                onChange={(e) => setFilters(prev => ({ ...prev, hasMedia: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">All Reviews</option>
                <option value="true">With Media</option>
                <option value="false">No Media</option>
              </select>

              {/* Refresh Button */}
              <button
                onClick={fetchReviews}
                className="flex items-center justify-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* Reviews List */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-orange-200 border-t-orange-600 rounded-full mx-auto mb-4" />
              <p className="text-gray-600">
                {isRequestInProgress ? 'Loading reviews...' : 'Preparing to load reviews...'}
              </p>
              {lastFetchTime > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  Last updated: {new Date(lastFetchTime).toLocaleTimeString()}
                </p>
              )}
            </div>
          ) : paginatedReviews.length > 0 ? (
            <div className="space-y-4 mb-6">
              {paginatedReviews.map(review => (
                <motion.div
                  key={review._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {renderStars(review.overallRating, 'w-5 h-5')}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {review.title || 'Untitled Review'}
                        </h3>
                        <p className="text-gray-700 mb-2 line-clamp-2">
                          {review.comment || 'No comment provided'}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>Customer: {getFullName(review.customerId) || 'Anonymous'}</span>
                          <span>•</span>
                          <span>Vendor: {getFullName(review.vendorId) || 'Unknown'}</span>
                          <span>•</span>
                          <span>{new Date(review.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {review.images && review.images.length > 0 && (
                        <div className="flex items-center text-sm text-gray-600">
                          <ImageIcon className="w-4 h-4 mr-1" />
                          {review.images.length}
                        </div>
                      )}
                      {renderStatusBadge(review.adminReview?.status || 'PENDING')}
                      <button
                        onClick={() => {
                          setSelectedReview(review);
                          setShowReviewModal(true);
                        }}
                        className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Reviews Found</h3>
              <p className="text-gray-600">No reviews match your current filters.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          )}

        {/* Review Modal */}
        <AnimatePresence>
          <ReviewModal
            review={selectedReview}
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
          />
        </AnimatePresence>

        {/* Flag Modal */}
        <AnimatePresence>
          <FlagModal />
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ReviewManagement;